import Vue from 'vue'
import Vuex from 'vuex'
import firebase from 'firebase'
import { vuexfireMutations, firestoreAction } from 'vuexfire'
import 'firebase/firestore'

import config from './config'
import ApiHelper from './mixins/ApiHelper'

firebase.initializeApp(config.firestore)

const db = firebase.firestore()

let channelRef = null
let usersRef = db.collection('users')
let submissionsRef = db.collection('submissions')
let transactionsRef = db.collection('transactions')
let bitsReceiptsRef = db.collection('bitsReceipts')
let giftSubBatchesRef = db.collection('giftSubBatches')
let mailsRef = db.collection('mails')
let lanesRef = db.collection('lanes')

Vue.use(Vuex)


export default new Vuex.Store({
  state: {
    account: {},
    users: [],
    usersSearch: [],
    submissions: [],
    bitsReceipts: [],
    // giftSubs: [],
    giftSubBatches: [],
    recentMails: [],
    usersRich: [],
    usersSpenders: [],
    lanes: [],
    rules: [],
    channels: [],
    channel: null,

    editUser: {},
    editUserTrx: [],
    editUserSubmissions: [],

    isLoading: false
  },

  mutations: {
    ...vuexfireMutations,

    resetState (state) {
      state.account = {}
      state.users = []
      state.usersSearch = []
      state.submissions = []
      state.bitsReceipts = []
      // state.giftSubs = []
      state.giftSubBatches = []
      state.recentMails = []
      state.usersRich = []
      state.usersSpenders = []
      state.lanes = []
      state.editUser = {}
      state.editUserTrx = []
      state.editUserSubmissions = []
      state.isLoading = false
    },

    resetTrx (state) {
      state.editUserTrx = []
    },

    setLoading (state, pos) {
      state.isLoading = !!pos
    },

    setRules (state, rules) {
      state.rules.splice(0, state.rules.length, ...rules)
    }
  },

  getters: {
    loggedIn ({ account }) {
      return account.name
    },

    isAdmin ({ account }) {
      return account.flags.admin
    },

    isPremium ({ channel }) {
      return channel && channel.premium === true
    },

    getUser ({ users }) {
      return (id) => users.find((user) => user.id === id)
    },

    getSubmission ({ submissions }) {
      return (id) => submissions.find((submission) => submission.id === id)
    },

    defaultLane ({ lanes }) {
      return lanes.find((lane) => lane.order === 0 && lane.enabled) || lanes[0]
    },

    getLaneById ({ lanes }) {
      return (id) => lanes.find((lane) => lane.id === id)
    }
  },

  actions: {
    editUser: firestoreAction(({ commit, state, getters, bindFirestoreRef, unbindFirestoreRef }, uid) => {
      commit('setLoading', true)
      const userDoc = usersRef.doc(uid)
      const userSubs = submissionsRef.where('authorId', '==', uid)

      const tasks = [
        bindFirestoreRef('editUser', userDoc),
        bindFirestoreRef('editUserSubmissions', userSubs)
      ]
      if (getters.isAdmin || state.account.id === uid) {
        const userTrx = transactionsRef.where('userId', '==', uid)
        tasks.push(bindFirestoreRef('editUserTrx', userTrx))
      } else {
        // @todo vuexfire bug again: unbind trace calls tries to invoke an
        // unwatch on something that doesn't exist
        try {
          unbindFirestoreRef('editUserTrx')
        } catch (err) {
          console.warn(err)
        }
        commit('resetTrx')
      }
      return Promise.all(tasks)
        .finally(() => commit('setLoading', false))
    }),

    searchUsers: firestoreAction(({ bindFirestoreRef }, str) => {
      const usersSearch = channelRef.collection('users')
        .where('name', '>=', str)
      return bindFirestoreRef('usersSearch', usersSearch)
    }),

    setChannel: firestoreAction(({ commit, dispatch, bindFirestoreRef }, channelId) => {
      channelRef = db.collection('channels').doc(String(channelId))
      usersRef = channelRef.collection('users')
      submissionsRef = channelRef.collection('submissions')
      transactionsRef = channelRef.collection('transactions')
      bitsReceiptsRef = channelRef.collection('bitsReceipts')
      giftSubBatchesRef = channelRef.collection('giftSubBatches')
      mailsRef = channelRef.collection('mails')
      lanesRef = channelRef.collection('lanes')

      const lanes = lanesRef
        .where('archived', '==', false)
      const usersRecent = usersRef
        .orderBy('updated', 'desc')
        .limit(50)
      const giftBatches = giftSubBatchesRef
        .orderBy('created', 'desc')
        .limit(20)
      const recentMails = mailsRef
        .orderBy('created', 'desc')
        .limit(20)
      const usersRich = usersRef
        .orderBy('balance', 'desc')
        .limit(11)
      const usersSpenders = usersRef
        .orderBy('spent', 'asc')
        .limit(11)
      const fetchRules = ApiHelper.methods.apiRequest('rules')
        .then(({ data }) => commit('setRules', data.rules))
        .catch((err) => console.error('Failed to fetch rules', err))
      return Promise.all([
        bindFirestoreRef('channel', channelRef),
        bindFirestoreRef('users', usersRecent),
        bindFirestoreRef('submissions', submissionsRef),
        bindFirestoreRef('bitsReceipts', bitsReceiptsRef),
        bindFirestoreRef('giftSubBatches', giftBatches),
        bindFirestoreRef('recentMails', recentMails),
        bindFirestoreRef('usersRich', usersRich),
        bindFirestoreRef('usersSpenders', usersSpenders),
        bindFirestoreRef('lanes', lanes),
        fetchRules
      ]).catch((err) => {
        console.error('setChannel err', err)
        return dispatch('logout')
      })
    }),

    login: firestoreAction(({ dispatch, bindFirestoreRef }, uid) => {
      const channels = db
        .collection('channels')
        .where('members', 'array-contains', uid)
      return dispatch('setChannel', uid)
        .then(() => bindFirestoreRef('account', usersRef.doc(uid)))
        .then(() => bindFirestoreRef('channels', channels))
        .catch((err) => console.log('Login failed', err))
    }),

    logout: firestoreAction(({ commit, unbindFirestoreRef }) => {
      return Promise.all([
        // unbindFirestoreRef('account'),
        // unbindFirestoreRef('users'),
        // unbindFirestoreRef('submissions'),
        // unbindFirestoreRef('bitsReceipts'),
        // // unbindFirestoreRef('giftSubs'),
        // unbindFirestoreRef('giftSubBatches'),
        // unbindFirestoreRef('recentMails'),
        // unbindFirestoreRef('usersRich'),
        // unbindFirestoreRef('usersSpenders'),
        // unbindFirestoreRef('lanes'),
        // unbindFirestoreRef('editUser'),
        // unbindFirestoreRef('editUserTrx'),
        // unbindFirestoreRef('editUserSubmissions')
      ])
      .then(() => commit('resetState'))
      .catch((err) => console.error('Unbinding failed during logout', err))
    })
  }
})
